/**
 * Hook that bridges nuqs URL state → SearchPage Redux duck.
 *
 * Reads all search params from nuqs and dispatches searchListings whenever
 * they change. Returns listings, pagination, and loading state from Redux.
 */
import { useEffect, useMemo } from 'react';
import { useSelector, useDispatch, shallowEqual } from 'react-redux';
import { parseAsString, parseAsInteger, useQueryStates } from 'nuqs';

import { useConfiguration } from '../../../context/configurationContext';
import { getListingsById } from '../../../ducks/marketplaceData.duck';
import { searchListings } from '../SearchPage.duck';

// Sharetribe sort values
export const SORT_OPTIONS = [
  { value: '', label: 'Relevance' },
  { value: '-createdAt', label: 'Newest' },
  { value: 'price', label: 'Price: Low to High' },
  { value: '-price', label: 'Price: High to Low' },
];

// Condition label → Sharetribe enum values
// Design uses display labels; we map to the API's multi-enum values.
export const CONDITION_GROUPS = [
  {
    label: 'New',
    values: ['leftover'],
    testId: 'button-filter-condition-new',
  },
  {
    label: 'Used',
    values: ['excellent', 'good', 'fair'],
    testId: 'button-filter-condition-used',
  },
  {
    label: 'Open Box',
    values: ['excellent'],
    testId: 'button-filter-condition-open-box',
  },
];

// Price preset → dollar string that the duck converts to cents
export const PRICE_PRESETS = [
  { label: 'Any', value: '' },
  { label: 'Under $500', value: '0,500' },
  { label: '$500 - $2K', value: '500,2000' },
  { label: '$2K+', value: '2000,100000' },
];

// Quick category chips shown below the search bar
export const QUICK_CATEGORIES = [
  'lumber',
  'roofing_siding',
  'tools',
  'electrical',
  'plumbing',
  'flooring',
];

// nuqs param schema: mirrors the URL params Sharetribe expects exactly
export const SEARCH_PARAMS_SCHEMA = {
  keywords: parseAsString.withDefault(''),
  pub_categoryLevel1: parseAsString.withDefault(''),
  pub_condition: parseAsString.withDefault(''),
  price: parseAsString.withDefault(''),
  address: parseAsString.withDefault(''),
  origin: parseAsString.withDefault(''),
  distance: parseAsString.withDefault(''),
  bounds: parseAsString.withDefault(''),
  sort: parseAsString.withDefault(''),
  page: parseAsInteger.withDefault(1),
};

export function useSearchPage() {
  const dispatch = useDispatch();
  const config = useConfiguration() as any;

  // Default history mode is 'replace': filter changes don't create browser history entries.
  // Callers that want a push (e.g. keyword submit) pass { history: 'push' } to setParams directly.
  const [params, setParams] = useQueryStates(SEARCH_PARAMS_SCHEMA);

  // Redux state
  const searchInProgress: boolean = useSelector((state: any) => state.SearchPage.searchInProgress);
  const searchError = useSelector((state: any) => state.SearchPage.searchListingsError);
  const pagination = useSelector((state: any) => state.SearchPage.pagination, shallowEqual);
  const resultIds = useSelector(
    (state: any) => state.SearchPage.currentPageResultIds,
    shallowEqual
  );
  const entities = useSelector((state: any) => state.marketplaceData?.entities, shallowEqual);
  const listings = useMemo(
    () => getListingsById({ marketplaceData: { entities } } as any, resultIds),
    [entities, resultIds]
  );

  // Re-fetch whenever URL params change.
  // We intentionally re-fetch even after SSR because the SSR loadData uses a different
  // field set that omits publicData.location, publicData.condition, publicData.categoryLevel1 etc.
  useEffect(() => {
    const { page, address, origin, distance, bounds, ...rest } = params;

    const originMaybe = origin ? { origin } : {};
    const distanceMaybe = origin && distance ? { distance } : {};
    const boundsMaybe = bounds ? { bounds } : {};

    const cleanRest = Object.fromEntries(
      Object.entries(rest).filter(([, v]) => v !== '')
    );

    dispatch(
      (searchListings as any)({
        searchParams: {
          ...cleanRest,
          ...originMaybe,
          ...distanceMaybe,
          ...boundsMaybe,
          page,
          perPage: 24,
          include: ['images', 'author'],
          'fields.listing': [
            'title',
            'geolocation',
            'price',
            'deleted',
            'state',
            'createdAt',
            'publicData.listingType',
            'publicData.transactionProcessAlias',
            'publicData.unitType',
            'publicData.condition',
            'publicData.location',
            'publicData.categoryLevel1',
            'publicData.categoryLevel2',
            'metadata.reviewAvg',
            'metadata.reviewCount',
          ],
          'fields.user': ['profile.metadata'],
          'fields.image': [
            'variants.listing-card',
            'variants.listing-card-2x',
            'variants.scaled-small',
            'variants.scaled-medium',
          ],
        },
        config,
      })
    );
  }, [JSON.stringify(params)]); // eslint-disable-line react-hooks/exhaustive-deps

  return {
    params,
    setParams,
    listings,
    pagination,
    searchInProgress,
    searchError,
  };
}
